import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_COLLECTION } from '@/modules/collection/router';
import { CollectionType } from '@/modules/collection/types';
import { collectionService } from '@/services/collection';
import useVuelidate from '@vuelidate/core';
import { minValue, required } from '@vuelidate/validators';
import { onMounted, reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useCollectionItemAddOrEdit() {
    const router = useAppRouter();
    const route = useRoute();
    const { executeAction, excuteAndNotify } = useCrudActions();

    const model = reactive({
        collectionId: StringUtils.toNumber(route.params.collectionId),
        collectionItemId: StringUtils.toNumber(route.params.collectionItemId),
        collectionType: StringUtils.toNumber(route.query.type),
        assetId: 0,
        sortOrder: 0,
        pieceCount: 0,
        piecePrevPercent: 0,
        pieceTwoBackPercent: 0,
        freeBoosterCharge: 0,

        get isUpdate() {
            return this.collectionItemId != null;
        },
        get pageLabel() {
            const aciontName = this.isUpdate ? 'Edit' : 'Create';
            const itemName = this.collectionType == CollectionType.Journey ? 'Level' : 'Puzzle';
            return aciontName + ' ' + itemName;
        }
    });

    const validations = {
        assetId: { required, minValue: minValue(1) },
        sortOrder: { required, minValue: minValue(1) },
        pieceCount: { required, minValue: minValue(1) },
        piecePrevPercent: { required, minValue: minValue(1) },
        pieceTwoBackPercent: { required, minValue: minValue(1) },
        freeBoosterCharge: { required, minValue: minValue(0) }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = executeAction({
        request: () => collectionService.item.get(model.collectionId, model.collectionItemId),
        onSuccess: ({ data }) => Object.assign(model, data),
        onError: () => router.push({ name: ROUTE_NAME_COLLECTION.INDEX(model.collectionType) })
    });

    const createOrUpdate = executeAction({
        validator: v$,
        request: () =>
            model.isUpdate
                ? collectionService.item.update(
                      model.collectionId,
                      model.collectionItemId,
                      model.assetId,
                      model.sortOrder
                  )
                : collectionService.item.create(model.collectionId, model.assetId, model.sortOrder),
        onSuccess: ({ data }) => {
            const collectionItemId = model.isUpdate ? model.collectionItemId : data;
            updateGameplay(collectionItemId);
        }
    });

    const updateGameplay = (collectionItemId: number) =>
        excuteAndNotify({
            request: () =>
                collectionService.item.updateGameplay(
                    model.collectionId,
                    collectionItemId,
                    model.pieceCount,
                    model.piecePrevPercent,
                    model.pieceTwoBackPercent,
                    model.freeBoosterCharge
                ),
            onSuccess: () =>
                router.push({
                    name: ROUTE_NAME_COLLECTION.ITEM.INDEX(model.collectionType),
                    params: { collectionId: model.collectionId }
                }),
            onError: () =>
                router.push({
                    name: ROUTE_NAME_COLLECTION.ITEM.EDIT(model.collectionType),
                    params: { collectionId: model.collectionId, collectionItemId }
                })
        })();

    onMounted(() => {
        // Edit page
        if (model.collectionItemId != null) {
            fetch();
        }
    });

    return { model, v$, createOrUpdate };
}
